import React, { ReactElement, useContext, useState } from 'react';
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import { useActionCriteria } from '../../utils/useActionProps';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { AssemblyPreBulkReceive } from '../../graphql/queries/product/AssemblyPreBulkReceiveQuery.graphql';
import { BulkAssemblyReceiveMutation } from '../../graphql/mutations/product/BulkAssemblyReceiveMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import {
  IAssemblyPreBulkReceiveQueryResult,
  IAssemblyReceiveRequirements,
  IBulkAssemblyReceiveMutationResult,
  IBulkAssemblyReceiveMutationVariables,
  IQueryPreBulkAssemblyReceiveArgs,
} from '../../graphql/schema/p4types';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { useLazyQuery } from '@apollo/client';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import AssemblyPreBulkReceiveModal from '../../views/product/AssemblyPreBulkReceiveModal';

enum AssemblyWorkspaceActionType {
  BulkReceive = 'BULK_RECEIVE_ASSEMBLIES',
}

export const assemblyWorkspaceActionsForPermissions: Array<IWorkspaceAction<AssemblyWorkspaceActionType>> = [
  {
    description: 'Bulk Receive Assemblies',
    actionReference: AssemblyWorkspaceActionType.BulkReceive,
    permissionLabel: 'Bulk Receive Assemblies',
  },
];

const useBulkReceiveAction = (): [IWorkspaceAction<AssemblyWorkspaceActionType>, ReactElement] => {
  const actionProps = useActionCriteria();
  const { reloadData } = useContext(WorkspaceContext);
  const [preBulkReceiveData, setPreBulkReceiveData] = useState<IAssemblyReceiveRequirements>({});
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [getPreBulkReceiveData] = useLazyQuery<IAssemblyPreBulkReceiveQueryResult, IQueryPreBulkAssemblyReceiveArgs>(
    AssemblyPreBulkReceive,
    {
      variables: {
        actionCriteria: actionProps,
      },
    }
  );

  const [bulkReceiveMutation] = useBackgroundMutation<
    IBulkAssemblyReceiveMutationResult,
    IBulkAssemblyReceiveMutationVariables
  >(BulkAssemblyReceiveMutation, {
    variables: {
      input: {
        clientMutationId: getClientMutationId(),
        actionCriteria: actionProps,
        assemblyWithReceipts: [],
      },
    },
    fetchPolicy: 'network-only',
    onCompleteJob: () => {
      reloadData?.();
    },
  });

  const bulkReceive = () => {
    bulkReceiveMutation({
      onCompleted: () => {
        onClose?.();
      },
    });
  };

  const onClick = () =>
    getPreBulkReceiveData().then((queryResult) => {
      const data = normalizeVariables(queryResult?.data?.preBulkAssemblyReceive) || {};
      if (data?.needsManualConfiguration?.length === 0) {
        bulkReceive();
      } else {
        setPreBulkReceiveData(data as IAssemblyReceiveRequirements);
        setModalOpen(true);
      }
    });

  const onClose = () => {
    setModalOpen(false);
  };

  return [
    {
      key: 'bulkReceiveAssemblies',
      text: 'Bulk Receive Assemblies',
      onClick,
      actionReference: AssemblyWorkspaceActionType.BulkReceive,
    },
    <AssemblyPreBulkReceiveModal modalOpen={modalOpen} preBulkReceiveDetails={preBulkReceiveData} onClose={onClose} />,
  ];
};

export const useAssemblyWorkspaceActions: () => [IWorkspaceAction<AssemblyWorkspaceActionType>[], ReactElement] =
  () => {
    const [bulkReceiveAction, bulkReceiveModal] = useBulkReceiveAction();
    const actions: IWorkspaceAction<AssemblyWorkspaceActionType>[] = [bulkReceiveAction];
    return [actions, <>{bulkReceiveModal}</>];
  };
